import { useEffect, useMemo, useRef, useState } from 'react';
import Avatar from '@/components/base/Avatar';
import Button from '@/components/base/Button';
import { formatLocalNumber, operatorById } from '@/utils/sendMoney';
import type { MoneyRecipient, SinglePayee } from '@/types/portal';

interface MobileAccountStepProps {
  dialCode: string;
  dialCodes: string[];
  onDialCode: (code: string) => void;
  number: string;
  onNumber: (value: string) => void;
  recents: MoneyRecipient[];
  favourites: MoneyRecipient[];
  payees: SinglePayee[];
  onPickRecipient: (recipient: MoneyRecipient) => void;
  onPickPayee: (payee: SinglePayee) => void;
  onContinue: () => void;
  canContinue: boolean;
  onManageFavourites: () => void;
}

/**
 * "Please select an account" — enter a mobile number (with a recents type-ahead)
 * or search a saved recipient and favourite list.
 */
export default function MobileAccountStep({
  dialCode,
  dialCodes,
  onDialCode,
  number,
  onNumber,
  recents,
  favourites,
  payees,
  onPickRecipient,
  onPickPayee,
  onContinue,
  canContinue,
  onManageFavourites,
}: MobileAccountStepProps) {
  const [focused, setFocused] = useState(false);
  const [search, setSearch] = useState('');
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDocMouseDown = (event: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) {
        setFocused(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, []);

  const matches = useMemo(() => {
    const digits = number.replace(/\D/g, '');
    if (!digits) return recents.slice(0, 6);
    return recents
      .filter(
        (item) =>
          item.number.includes(digits) ||
          formatLocalNumber(item.number, dialCode).replace(/\s/g, '').includes(digits),
      )
      .slice(0, 6);
  }, [number, recents, dialCode]);

  const results = useMemo(() => {
    const text = search.trim().toLowerCase();
    if (!text) return [];
    const payeeHits = payees
      .filter(
        (item) =>
          item.name.toLowerCase().includes(text) ||
          (item.handle ?? '').toLowerCase().includes(text),
      )
      .slice(0, 4)
      .map((item) => ({ key: `p-${item.id}`, payee: item, recipient: null as MoneyRecipient | null }));
    const recipientHits = recents
      .filter((item) => item.name.toLowerCase().includes(text) || item.number.includes(text))
      .slice(0, 4)
      .map((item) => ({ key: `r-${item.id}`, payee: null as SinglePayee | null, recipient: item }));
    return [...payeeHits, ...recipientHits];
  }, [search, payees, recents]);

  const showRecents = focused && matches.length > 0;

  return (
    <div className="space-y-6">
      <h3 className="font-heading text-lg font-semibold text-foreground-950">
        Please select an account
      </h3>

      <div ref={wrapRef} className="relative">
        <label htmlFor="flow-mobile" className="text-xs font-label font-medium text-foreground-500">
          Mobile number
        </label>
        <div className="mt-1 flex items-end gap-3">
          <select
            aria-label="Country code"
            value={dialCode}
            onChange={(event) => onDialCode(event.target.value)}
            className="h-9 shrink-0 cursor-pointer border-b border-background-300 bg-transparent text-sm text-foreground-600 focus:border-primary-500 focus:outline-none"
          >
            {dialCodes.map((code) => (
              <option key={code} value={code}>
                {code}
              </option>
            ))}
          </select>
          <input
            id="flow-mobile"
            value={number}
            inputMode="numeric"
            onChange={(event) => onNumber(event.target.value.replace(/\D/g, ''))}
            onFocus={() => setFocused(true)}
            placeholder="712 345 678"
            className="h-9 flex-1 border-b border-background-300 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-500 focus:outline-none"
          />
          <Button size="sm" onClick={onContinue} disabled={!canContinue} className="mb-0.5">
            Continue
          </Button>
        </div>

        {showRecents ? (
          <ul className="absolute z-20 mt-1 w-full overflow-hidden rounded-md border border-background-200 bg-background-50">
            {matches.map((item) => (
              <li key={item.id} className="border-b border-background-100 last:border-0">
                <button
                  type="button"
                  onClick={() => {
                    onPickRecipient(item);
                    setFocused(false);
                  }}
                  className="flex w-full cursor-pointer items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-background-100"
                >
                  <span className="flex-1 text-sm text-foreground-900">
                    {formatLocalNumber(item.number, dialCode)}
                  </span>
                  <span className="text-xs text-foreground-500">
                    {operatorById(item.operatorId)?.name}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      <div className="border-t border-background-100 pt-5">
        <div className="relative">
          <span className="pointer-events-none absolute left-4 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-base text-foreground-400">
            <i className="ri-search-line" />
          </span>
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search for a recipient"
            className="h-10 w-full rounded-full border border-background-300 bg-background-50 pl-10 pr-4 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
          />
        </div>

        {results.length > 0 ? (
          <ul className="mt-3 divide-y divide-background-100 overflow-hidden rounded-md border border-background-200">
            {results.map((hit) => (
              <li key={hit.key}>
                <button
                  type="button"
                  onClick={() => {
                    if (hit.payee) onPickPayee(hit.payee);
                    if (hit.recipient) onPickRecipient(hit.recipient);
                    setSearch('');
                  }}
                  className="flex w-full cursor-pointer items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-background-100"
                >
                  <Avatar
                    src={hit.payee?.avatar ?? hit.recipient?.avatar ?? ''}
                    alt={hit.payee?.name ?? hit.recipient?.name ?? 'Recipient'}
                    size="sm"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-foreground-950">
                      {hit.payee?.name ?? hit.recipient?.name}
                    </span>
                    <span className="block truncate text-xs text-foreground-500">
                      {hit.payee
                        ? hit.payee.invoices.length > 0
                          ? hit.payee.invoices.map((invoice) => invoice.campaign).join(' · ')
                          : 'One-off payee'
                        : hit.recipient?.number}
                    </span>
                  </span>
                  <i className="ri-arrow-right-s-line text-foreground-400" />
                </button>
              </li>
            ))}
          </ul>
        ) : null}

        <div className="mt-5 flex items-center justify-between">
          <span className="text-[11px] font-label font-semibold uppercase tracking-wider text-foreground-400">
            Favourites
          </span>
          <button
            type="button"
            onClick={onManageFavourites}
            className="cursor-pointer text-xs font-label font-medium text-primary-700 transition-colors hover:text-primary-800"
          >
            Manage
          </button>
        </div>

        <ul className="mt-2 divide-y divide-background-100">
          {favourites.map((item) => (
            <li key={item.id}>
              <button
                type="button"
                onClick={() => onPickRecipient(item)}
                className="flex w-full cursor-pointer items-center gap-3 py-2.5 text-left"
              >
                <Avatar src={item.avatar} alt={item.name} size="sm" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-foreground-950">
                    {item.name}
                  </span>
                  <span className="block text-xs text-foreground-500">
                    {formatLocalNumber(item.number, dialCode)} ·{' '}
                    {operatorById(item.operatorId)?.name}
                  </span>
                </span>
                <i className="ri-star-fill text-accent-500" />
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}